import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useEntity } from "./EntityContext";

const PortfolioConfigContext = createContext();

export const usePortfolioConfig = () => {
  const context = useContext(PortfolioConfigContext);
  if (!context) {
    throw new Error(
      "usePortfolioConfig must be used within a PortfolioConfigProvider"
    );
  }
  return context;
};

export const PortfolioConfigProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentEntity } = useEntity();

  // Default configuration
  const [config, setConfig] = useState({
    dataSources: {
      bice: true, // Currently available
      sura: false, // Placeholder for future implementation
    },
  });

  // Load configuration from localStorage and Firebase
  useEffect(() => {
    const loadConfig = async () => {
      if (!currentUser || !currentEntity) return;

      try {
        // Load from localStorage first (for immediate UI response)
        const localConfig = localStorage.getItem(
          `portfolio.config.${currentUser.uid}.${currentEntity.id}`
        );
        if (localConfig) {
          const parsed = JSON.parse(localConfig);
          setConfig((prev) => ({ ...prev, ...parsed }));
        }

        // TODO: Load from Firebase when we implement the service
        // const firebaseConfig = await getPortfolioSettings(currentUser.uid, currentEntity.id);
        // if (firebaseConfig) {
        //   setConfig(prev => ({ ...prev, ...firebaseConfig }));
        // }
      } catch (error) {
        console.error("Error loading portfolio config:", error);
      }
    };

    loadConfig();
  }, [currentUser, currentEntity]);

  // Save configuration to localStorage and Firebase
  const updateConfig = async (newConfig) => {
    if (!currentUser || !currentEntity) return;

    try {
      const updatedConfig = { ...config, ...newConfig };
      setConfig(updatedConfig);

      // Save to localStorage
      localStorage.setItem(
        `portfolio.config.${currentUser.uid}.${currentEntity.id}`,
        JSON.stringify(updatedConfig)
      );

      // TODO: Save to Firebase when we implement the service
      // await upsertPortfolioSettings(currentUser.uid, currentEntity.id, updatedConfig);
    } catch (error) {
      console.error("Error saving portfolio config:", error);
    }
  };

  // Update specific data source
  const updateDataSource = async (source, enabled) => {
    await updateConfig({
      dataSources: {
        ...config.dataSources,
        [source]: enabled,
      },
    });
  };

  // Get available data sources (for UI display)
  const getAvailableDataSources = () => {
    return [
      {
        id: "bice",
        name: "Banco Bice",
        description: "Datos de portafolio desde Banco Bice",
        available: true,
        enabled: config.dataSources.bice,
      },
      {
        id: "sura",
        name: "Sura",
        description: "Datos de portafolio desde Sura (próximamente)",
        available: false,
        enabled: config.dataSources.sura,
      },
    ];
  };

  const value = {
    config,
    updateConfig,
    updateDataSource,
    getAvailableDataSources,
  };

  return (
    <PortfolioConfigContext.Provider value={value}>
      {children}
    </PortfolioConfigContext.Provider>
  );
};
